"use client";

import React from "react";
import NextLink from "next/link";
import { useParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Link, Typography } from "@mui/material";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { useSellableSets } from "./useArtSets";
import { setPath } from "./art-sets";
import { localized } from "./artwork-types";

/**
 * "This work is also in the set …" on a work's own page — one line per set
 * the work belongs to, and only sets that can be bought right now.
 */
export default function ArtworkSetLink({ artworkSlug }: { artworkSlug: string }) {
  const t = useTranslations("art");
  const locale = useLocale();
  const params = useParams();
  const localeSegment = (params?.locale as string) ?? locale;
  const { sets } = useSellableSets(artworkSlug);
  if (!sets.length) return null;
  return (
    <>
      {sets.map(({ set }) => (
        <Typography
          key={set.slug}
          variant="body2"
          sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}
        >
          <CollectionsOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <span>
            {t.rich("sets.inSet", {
              title: localized(set.title, locale),
              link: (chunks) => (
                <Link
                  component={NextLink}
                  href={setPath(localeSegment, set.slug)}
                  sx={{ fontWeight: 600 }}
                >
                  {chunks}
                </Link>
              ),
            })}
          </span>
        </Typography>
      ))}
    </>
  );
}
